import { useQuery } from '@tanstack/react-query';
import ms from 'ms';

import { toast } from '@hivebrite/design-system/Toast';

import { dynamicTopicResourcePath } from 'hb-react/shared/apps/Topics/utils';
import { networkToTopicIdentifiersMap } from 'hb-react/shared/constants/pageIdentifiers';
import { type PageIdentifier } from 'hb-react/shared/constants/pageIdentifiers';
import { axiosInstance } from 'hb-react/shared/utils/axios';
import * as errorReporting from 'hb-react/shared/utils/errorReporting';
import { type AppPageConfig } from 'hb-react/types';

const getTopicIdentifier = (identifier) =>
  networkToTopicIdentifiersMap[identifier] ?? identifier;

const getPageConfigPath = ({ identifier, topicId, whitelabelURL }) => {
  if (topicId) {
    return dynamicTopicResourcePath(
      Routes.topic_page_path(topicId, getTopicIdentifier(identifier), {
        format: 'json',
      }),
      {
        whitelabelURL,
        topicId,
      },
    );
  }

  return Routes.network_page_customizable_json_path(identifier);
};

interface Props {
  enabled?: boolean;
  identifier: PageIdentifier | string;
  initialData?: AppPageConfig;
  topicId?: string | null;
  whitelabelURL?: string;
}

export const usePageConfigQuery = ({
  enabled = true,
  identifier,
  initialData,
  topicId = null,
  whitelabelURL,
}: Props) =>
  useQuery({
    queryKey: ['pageConfig', topicId, identifier],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get<{
          page_customizable: AppPageConfig;
        }>(getPageConfigPath({ identifier, topicId, whitelabelURL }), {
          params: {
            native_page: true,
          },
        });

        return response.data.page_customizable;
      } catch (error) {
        toast.error(__('Sorry, an error occurred'));
        errorReporting.captureException(error);

        throw error;
      }
    },
    enabled: !!identifier && enabled,
    gcTime: Infinity,
    initialData,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: ms('15m'),
  });
